/**
 * dlcService.js
 *
 * Frontend client for the DLC (Development Lifecycle) BFF API.
 * Handles all state that is not embedded in requirements.md:
 * PR links, agent configuration, and implementation run history.
 */

import config from '../config';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

const bffBase = () => config.bffUrl || '';

// ---------------------------------------------------------------------------
// Requirements DLC state
// ---------------------------------------------------------------------------

/**
 * Fetch DLC state for all requirements in a project+branch.
 *
 * @param {string} projectId
 * @param {string} [branch='main']
 * @returns {Promise<Object>} Map of reqId → dlcState
 */
export async function getDlcRequirementsState(projectId, branch = 'main') {
  try {
    const qs = new URLSearchParams({ projectId, branch });
    const res = await getAuthorizedFetch()(`${bffBase()}/api/dlc/requirements?${qs}`, {
      headers: getBffHeaders(),
    });
    if (!res.ok) return {};
    const data = await res.json();
    // Return as a map keyed by reqId for fast lookup
    const map = {};
    for (const item of data.requirements || []) {
      map[item.reqId] = item;
    }
    return map;
  } catch {
    return {};
  }
}

/**
 * Update DLC state for a single requirement.
 *
 * @param {string} projectId
 * @param {string} reqId
 * @param {Object} patch - Partial state to update
 * @returns {Promise<Object>}
 */
export async function patchDlcRequirement(projectId, reqId, patch) {
  const res = await fetch(`${bffBase()}/api/dlc/requirements/${encodeURIComponent(reqId)}`, {
    method: 'PATCH',
    headers: getBffHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ projectId, ...patch }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update requirement ${reqId}`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Implementation runs
// ---------------------------------------------------------------------------

/**
 * Record a new implementation run for a requirement.
 *
 * @param {string} projectId
 * @param {string} reqId
 * @param {{ agentName: string, swiId?: string }} config
 * @returns {Promise<Object>}
 */
export async function createImplementationRun(projectId, reqId, agentConfig) {
  const res = await fetch(`${bffBase()}/api/dlc/implementations`, {
    method: 'POST',
    headers: getBffHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ projectId, reqId, agentConfig }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to create implementation run');
  }
  return res.json();
}

/**
 * Get implementation run history for a requirement.
 *
 * @param {string} reqId
 * @returns {Promise<Object[]>}
 */
export async function getImplementationRuns(reqId) {
  try {
    const res = await getAuthorizedFetch()(`${bffBase()}/api/dlc/implementations/${encodeURIComponent(reqId)}`, {
      headers: getBffHeaders(),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.runs || [];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Available agents (reuse existing /api/tools endpoint)
// ---------------------------------------------------------------------------

/**
 * Fetch AI agents available for implementation.
 * Returns a simplified list [{ id, name, description, model }].
 *
 * @returns {Promise<Array>}
 */
export async function getAvailableAgents() {
  try {
    const agentUrl = config.agentUrl || '';
    const res = await getAuthorizedFetch()(`${agentUrl}/api/agents`, {
      headers: getBffHeaders(),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.data?.agents) {
        return data.data.agents;
      }
    }
    // Fallback: static list matching what's shown in the screenshots
    return getDefaultAgents();
  } catch {
    return getDefaultAgents();
  }
}

function getDefaultAgents() {
  return [
    { id: 'backend-feature-developer', name: 'Backend Feature Developer', model: 'Anthropic — Claude Opus 4.6' },
    { id: 'frontend-ui-implementer', name: 'Frontend UI Implementer', model: 'Anthropic — Claude Sonnet 4.2' },
    { id: 'api-integration-specialist', name: 'API Integration Specialist', model: 'OpenAI — o3-pro' },
    { id: 'security-hardening-agent', name: 'Security Hardening Agent', model: 'Google DeepMind — Gemini 2.5 Pro' },
  ];
}
